import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PackageCheck, Table, Database, Upload } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import StockPlanningTab from '../../call-off/stock-planning/StockPlanningTab';
import SupplierStockPlanningTab from '../../call-off/stock-planning/SupplierStockPlanningTab';
import CallOffManagementTab from '../../call-off/management/CallOffManagementTab';
import PartRepositoryTab from '../../call-off/parts/PartRepositoryTab';
import StockUploadTab from '../../call-off/stock-upload/StockUploadTab';

/** CR-8613: `?call_off_view=management` opens Call Off Management (the list). */
export const CALL_OFF_VIEW_PARAM = 'call_off_view';

const CUSTOMER_TABS = [
  { id: 'stock-planning', label: 'Stock Planning', icon: Table },
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
  { id: 'parts', label: 'Parts List', icon: Database },
] as const;

// CR-7732: the haulier pickup OTIF report is built (call-off/haulier/
// HaulierPerformanceTab, behind /call-off/call-offs/haulier-otif/) but is
// deliberately not wired to a tab: the ticket defers where it is shown. Drop
// it into whichever surface is chosen — it takes no props.

const SUPPLIER_TABS = [
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
  { id: 'supplier-stock-planning', label: 'Stock Planning', icon: Table },
  { id: 'stock-upload', label: 'Stock Upload', icon: Upload },
] as const;

// CR-7732: a supplier acting as a haulier keeps the Call Off tab but not the
// stock ones — it holds no stock of its own to plan or upload. Same single
// tab a logistics company gets.
const CARRIER_TABS = [
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
] as const;

const LOGISTICS_TABS = [
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
] as const;

type TabId = 'stock-planning' | 'supplier-stock-planning' | 'management' | 'parts' | 'stock-upload';

/**
 * Call Off tab container rendered inside the Purchasing page.
 * Customers see 3 inner tabs (Stock Planning, Management, Parts).
 * Suppliers see 2 inner tabs (Management, Stock Upload).
 * Logistics users see 1 inner tab (Management) — call-offs further filtered
 * to confirmed/dispatched/delivered by CallOffViewSet.get_queryset().
 * A supplier a customer marked as its Logistics Provider (``logistics_view``)
 * carries for that customer and receives no call-offs of its own, so it sees
 * the carrier view in place of the supplier tabs.
 */
export default function CallOffTab() {
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLogistics = user?.company_type === 'logistics';
  const isSupplier = user?.platform === 'SUPPLIER';
  // CR-7732: a supplier acting as a haulier is a carrier here, whatever its
  // own orders look like elsewhere.
  const isCarrier = isLogistics || (isSupplier && isFeatureEnabled('logistics_view'));
  const tabs = isLogistics
    ? LOGISTICS_TABS
    : isCarrier
      ? CARRIER_TABS
      : isSupplier ? SUPPLIER_TABS : CUSTOMER_TABS;
  // Auto-switch to management sub-tab when navigating from a notification —
  // or, CR-8613, from the sidebar's Call Off item, which opens the list.
  const hasCallOffId = searchParams.has('calloff_id');
  const openList = searchParams.get(CALL_OFF_VIEW_PARAM) === 'management';
  const defaultTab: TabId = isCarrier || isSupplier || hasCallOffId || openList
    ? 'management' : 'stock-planning';
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);

  // CR-8613: the sidebar item opens the list even when this tab is already
  // open on another sub-tab. The parameter is consumed, so the next click on
  // the item (the same address again) is seen as a new request.
  useEffect(() => {
    if (!openList) return;
    setActiveTab('management');
    const next = new URLSearchParams(searchParams);
    next.delete(CALL_OFF_VIEW_PARAM);
    setSearchParams(next, { replace: true });
  }, [openList]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
        <TabsList>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="stock-planning" className="mt-4">
          <StockPlanningTab />
        </TabsContent>

        <TabsContent value="supplier-stock-planning" className="mt-4">
          <SupplierStockPlanningTab />
        </TabsContent>

        <TabsContent value="management" className="mt-4">
          <CallOffManagementTab />
        </TabsContent>

        <TabsContent value="parts" className="mt-4">
          <PartRepositoryTab />
        </TabsContent>

        <TabsContent value="stock-upload" className="mt-4">
          <StockUploadTab />
        </TabsContent>

      </Tabs>
    </div>
  );
}
